/**
 * EFFET : une partie entre amis. Relie le runtime du jeu au réseau (PeerLink),
 * à la détection locale (profil rire/cligne choisi par l'hôte), à la
 * surveillance des incidents et à l'affichage de chaque phase.
 */
import {
  createBarbichetteOverlay,
  e2ePinnedLevel,
  type RemoteFace,
} from '../ar/barbichetteOverlay';
import { barbichetteActive } from '../ar/handPresence';
import { SLAP_MS } from '../ar/slap';
import { createThrowBuffer, type Throw } from '../chifoumi/gesture';
import { THROW_EMOJI } from '../chifoumi/rules';
import { baselineOf, createImitationTracker, type ImitationTracker } from '../smiley/score';
import { SMILEYS, smileyAt, type SmileyId } from '../smiley/smileys';
import { createSlapLayer } from '../ar/slapPlayer';
import { createLocalHandSource, createRemoteFaceSource } from '../detection/localFaceSource';
import { createPinchWatcher, type PinchStatus } from '../detection/pinchWatcher';
import type { MonitorEvent } from '../detection/expressionMonitor';
import { createExpressionMonitor, type ExpressionMonitor } from '../detection/expressionMonitor';
import { profileFor } from '../detection/profiles';
import type { Blendshapes, FaceFrame, Point3 } from '../detection/types';
import { chantLines, chantProgress, CHIFOUMI_CHANT } from '../game/chant';
import { DEFAULT_INCIDENT_THRESHOLDS, type MediaHealth } from '../game/incidents';
import { createIncidentWatcher } from '../game/incidentWatcher';
import { MAX_SNAPSHOT_CHARS, type GameKind, type GameMode, type Message } from '../game/protocol';
import { createGameRuntime } from '../game/runtime';
import { hidesVideo, matchWinner, punished, viewChanged } from '../game/view';
import { createInitialState, TIMINGS, type GameState, type Role } from '../game/stateMachine';
import { createLinkMonitor } from '../net/linkMonitor';
import type { PeerLink } from '../net/signaling';
import { createClipAssembler, toChunks } from '../replay/chunks';
import { decodeClip, encodeClip, MAX_CLIP_BYTES, type Clip } from '../replay/clipCodec';
import { createReplayPlayer, type ReplayPlayer } from '../replay/player';
import { createReplayRecorder } from '../replay/recorder';
import { replayTarget, type ReplayTarget } from '../replay/target';
import { h, mount } from './dom';
import { createSlapSound } from './slapSound';
import { calibrationMessage, TEXTS } from './messages';
import { createStage } from './stage';

const HEARTBEAT_MS = 500;
const PING_MS = 2000;
const HEALTH_MS = 250;
/**
 * Laisse le temps de voir le ralenti : 3 s filmées lues à mi-vitesse (6 s), plus
 * sa compression et son transfert (jusqu'à ~3 s sur une machine ou un réseau lents).
 */
const NEXT_ROUND_MS = 10_000;
/** Chifoumi : coup suivant (ni tapette ni ralenti avant la fin du match). */
const NEXT_THROW_MS = 3000;
/** Imite ce smiley : manche suivante (le temps de voir les photos et les scores). */
const NEXT_SMILEY_MS = 5000;
/** Imite ce smiley : au plus une photo de la meilleure grimace tous les… */
const SMILEY_PHOTO_MS = 250;
const SMILEY_COUNTDOWN: readonly (readonly string[])[] = [['3…', '2…', '1…']];
const REPLAY_MS = 3000;
const REPLAY_WAIT_MS = 6000;
const LOST_AFTER_MS = DEFAULT_INCIDENT_THRESHOLDS.heartbeatMs;

const MODE_LABEL: Record<GameMode, string> = {
  laugh: 'Le premier qui rit a perdu.',
  blink: 'Le premier qui cligne des yeux a perdu.',
};
const CHIFOUMI_LABEL = 'Chifoumi : pierre, feuille ou ciseaux au « MI ! ». Deux coups gagnants.';
const SMILEY_LABEL =
  'Imite ce smiley : 3 manches, 10 s pour imiter chaque smiley. Le plus ressemblant gagne.';
const ruleLabel = (s: GameState): string =>
  s.game === 'chifoumi' ? CHIFOUMI_LABEL : s.game === 'smiley' ? SMILEY_LABEL : MODE_LABEL[s.mode];
/** Smiley de la manche en cours (pendant le jeu) ou de la dernière tranchée (au résultat). */
const smileyOf = (s: GameState): SmileyId =>
  smileyAt(s.seed, s.match, s.phase.name === 'result' ? s.played - 1 : s.played);
const THROW_NAME: Record<Throw, string> = {
  rock: 'pierre',
  paper: 'feuille',
  scissors: 'ciseaux',
};

export interface GameOptions {
  readonly role: Role;
  /** Jeu et mode choisis par l'hôte (l'invité les apprend par `hello`). */
  readonly game: GameKind;
  readonly mode: GameMode;
  /** Demande de retour à l'accueil (l'application confirme si la partie est en cours). */
  readonly onQuit: () => void;
}

export interface GameHandle {
  /** Partie à deux en cours (quitter préviendra l'ami). */
  inProgress(): boolean;
  /** Quitter volontairement : l'ami est prévenu. */
  quit(): void;
  stop(): void;
}

export function startGame(
  root: HTMLElement,
  link: PeerLink,
  localStream: MediaStream,
  options: GameOptions,
): GameHandle {
  const stage = createStage(link, localStream);
  const status = h('p', { class: 'status', testid: 'status', role: 'status' }, 'Connecté !');
  const score = h('p', { class: 'score', testid: 'score' });
  const banner = h('section', { class: 'card banner', testid: 'banner', 'aria-live': 'polite' });
  const stats = h('p', { class: 'muted', testid: 'link-stats' });
  const meshStats = h('p', { class: 'muted', testid: 'mesh-stats' });
  const quitButton = h(
    'button',
    { type: 'button', class: 'button secondary', testid: 'quit' },
    'Quitter la partie',
  );
  quitButton.addEventListener('click', options.onQuit);
  let filterOn = true;
  const filterInput = h('input', { type: 'checkbox', testid: 'filter-toggle' });
  filterInput.checked = true;
  filterInput.addEventListener('change', () => {
    filterOn = filterInput.checked;
  });
  let soundOn = true;
  const soundInput = h('input', { type: 'checkbox', testid: 'sound-toggle' });
  soundInput.checked = true;
  soundInput.addEventListener('change', () => {
    soundOn = soundInput.checked;
  });
  const filterToggle = h('label', { class: 'toggle' }, filterInput, ' Filtre barbichette');
  stage.controls.append(
    filterToggle,
    h('label', { class: 'toggle' }, soundInput, ' Bruit de la tapette'),
    quitButton,
  );
  // --- Tapette ----------------------------------------------------------------------------
  // Au verdict : chez le gagnant sur l'image de l'ami, chez le perdant sur la sienne.
  const slapLayers = {
    remote: createSlapLayer(stage.remoteFigure, 'remote'),
    local: createSlapLayer(stage.localFigure, 'local'),
  };
  stage.remoteFigure.append(slapLayers.remote.element);
  stage.localFigure.append(slapLayers.local.element);
  const slapSound = createSlapSound();
  mount(root, status, score, banner, stage.element, stage.controls, stats, meshStats);

  const cleanups: (() => void)[] = [];
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (ms: number, fn: () => void): (() => void) => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return () => {
      clearTimeout(id);
      timers.delete(id);
    };
  };

  // --- Ralenti -------------------------------------------------------------------------
  // Chacun n'envoie que SON ralenti, et seulement s'il a perdu (ou disparu du cadre).
  const recorder = createReplayRecorder({
    video: stage.localVideo,
    faceSource: stage.faceSource,
    kind: () => stage.shareMode(),
  });
  const clips: Record<ReplayTarget['who'], { round: number; clip: Clip } | null> = {
    me: null,
    them: null,
  };
  let inbox: { round: number; assembler: ReturnType<typeof createClipAssembler> } | null = null;
  const replaySlot = h('div', { class: 'replay-slot', testid: 'replay-slot' });
  let replayKey: string | null = null;
  let player: ReplayPlayer | null = null;
  let cancelReplayWait: (() => void) | null = null;

  let sharedRound: number | null = null;
  let disposed = false;
  const shareReplay = (round: number): void => {
    if (sharedRound === round) return;
    sharedRound = round;
    void recorder.clip(REPLAY_MS).then((clip) => {
      if (disposed || clip.frames.length === 0) return;
      let bytes: Uint8Array;
      try {
        bytes = encodeClip(clip);
      } catch {
        return; // trop lourd : pas de ralenti plutôt qu'un canal engorgé
      }
      clips.me = { round, clip };
      for (const chunk of toChunks(bytes, round)) link.send(chunk);
      showReplay(replayTarget(runtime.state.phase, runtime.state.round));
    });
  };

  const clearReplay = (): void => {
    player?.stop();
    player = null;
    cancelReplayWait?.();
    cancelReplayWait = null;
    replaySlot.replaceChildren();
  };

  const showReplay = (target: ReplayTarget | null): void => {
    const key = target && `${target.round}:${target.who}`;
    if (key === replayKey && (player || !target)) return;
    if (key !== replayKey) clearReplay();
    replayKey = key;
    if (!target) return;
    const stored = clips[target.who];
    if (stored?.round === target.round) {
      clearReplay();
      player = createReplayPlayer(stored.clip);
      replaySlot.replaceChildren(h('p', { class: 'muted' }, 'Au ralenti :'), player.element);
      return;
    }
    if (cancelReplayWait) return;
    replaySlot.replaceChildren(h('p', { class: 'muted' }, 'Ralenti en chargement…'));
    cancelReplayWait = later(REPLAY_WAIT_MS, () => {
      if (player) return;
      replaySlot.replaceChildren(
        h('p', { class: 'muted', testid: 'replay-unavailable' }, 'Ralenti indisponible'),
      );
    });
  };

  const onReplayChunk = (m: Extract<Message, { type: 'replayChunk' }>): void => {
    if (inbox?.round !== m.round)
      inbox = { round: m.round, assembler: createClipAssembler(m.round, MAX_CLIP_BYTES) };
    const bytes = inbox.assembler.push(m);
    const clip = bytes && decodeClip(bytes);
    if (!clip) return;
    clips.them = { round: m.round, clip };
    showReplay(replayTarget(runtime.state.phase, runtime.state.round));
  };

  // --- Engagement par geste ------------------------------------------------------------
  // Pincement pouce-index tenu 0,3 s, n'importe où dans l'image ; le bouton n'apparaît
  // qu'en secours (délai de l'engagement). La main n'est suivie que pendant l'engagement.
  const handSource = createLocalHandSource(stage.localVideo);
  const pinch = createPinchWatcher();
  const pinchBar = h('span', { class: 'pinch-bar' });
  const pinchText = h('span', {});
  const pinchStatus = h(
    'p',
    { class: 'pinch-status', testid: 'pinch-status', role: 'status' },
    pinchText,
    pinchBar,
  );
  const PINCH_TEXT = {
    nohand: 'Montre ta main à la caméra',
    hand: 'Main vue : pince !',
    pinching: 'Tiens bon…',
  } as const;
  const showPinch = (st: PinchStatus): void => {
    const state = st.pinching ? 'pinching' : st.hand ? 'hand' : 'nohand';
    if (pinchStatus.dataset['state'] !== state) {
      pinchStatus.dataset['state'] = state;
      pinchText.textContent = PINCH_TEXT[state];
    }
    pinchBar.style.setProperty('--progress', String(st.progress));
  };
  let offHand: (() => void) | null = null;
  // --- Chifoumi : photo prise au verrouillage du geste, révélée au résultat -------------
  const SNAPSHOT_WIDTH = 320;
  const JPEG = 'data:image/jpeg;base64,';
  const snapshots: Record<'me' | 'them', { round: number; url: string } | null> = {
    me: null,
    them: null,
  };
  const snapshotImgs = {
    // Ma photo inversée comme mon retour vidéo ; celle de l'ami à l'endroit, comme sa vidéo.
    me: h('img', { testid: 'snapshot-me', alt: 'Ton geste', class: 'mirrored', hidden: '' }),
    them: h('img', { testid: 'snapshot-them', alt: 'Le geste de ton ami', hidden: '' }),
  };
  const snapshotsEl = h(
    'div',
    { class: 'snapshots' },
    h('figure', {}, snapshotImgs.me, h('figcaption', {}, 'Toi')),
    h('figure', {}, snapshotImgs.them, h('figcaption', {}, 'Ton ami')),
  );
  const showSnapshots = (round: number): void => {
    for (const who of ['me', 'them'] as const) {
      const snap = snapshots[who];
      const img = snapshotImgs[who];
      img.hidden = snap?.round !== round;
      if (snap?.round === round && img.src !== snap.url) img.src = snap.url;
    }
  };
  /** Image de ma caméra en JPEG (base64), assez légère pour un seul message. */
  const takeSnapshot = (): string | null => {
    const video = stage.localVideo;
    if (!video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = SNAPSHOT_WIDTH;
    canvas.height = Math.round((SNAPSHOT_WIDTH * video.videoHeight) / video.videoWidth);
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.7, 0.5, 0.3]) {
      const data = canvas.toDataURL('image/jpeg', quality).slice(JPEG.length);
      if (data.length <= MAX_SNAPSHOT_CHARS) return data;
    }
    return null;
  };

  // --- Imite ce smiley : visage au repos (décompte), jauge en direct, photo du meilleur moment --
  let baselineFrames: (Blendshapes | null)[] = [];
  let tracker: { round: number; imitation: ImitationTracker } | null = null;
  let bestPhoto: { round: number; data: string } | null = null;
  let lastPhotoAt = -Infinity;
  let imitateStart = 0;
  const smileyEmoji = h('p', { class: 'smiley-emoji', testid: 'smiley' });
  const smileyLabel = h('p', { class: 'smiley-label' });
  const liveText = h('span', {});
  const liveBar = h('span', { class: 'pinch-bar' });
  const liveEl = h(
    'p',
    { class: 'pinch-status', testid: 'imitation-live', role: 'status' },
    liveText,
    liveBar,
  );
  const bestEl = h('p', { class: 'muted', testid: 'imitation-best' });
  const timeLeft = h('p', { class: 'muted', testid: 'imitation-time' });
  const showLive = (live: number, best: number, now: number): void => {
    liveEl.dataset['score'] = String(live);
    liveText.textContent = `Ressemblance : ${live} %`;
    liveBar.style.setProperty('--progress', String(live / 100));
    bestEl.dataset['best'] = String(best);
    bestEl.textContent = `Ton meilleur (tenu) : ${best} %`;
    const left = Math.max(0, Math.ceil((TIMINGS.imitateMs - (now - imitateStart)) / 1000));
    timeLeft.textContent = `Encore ${left} s`;
  };
  const onSmileyFrame = (frame: FaceFrame): void => {
    const s = runtime.state;
    if (s.phase.name === 'chant') {
      baselineFrames.push(frame.blendshapes);
      return;
    }
    if (s.phase.name !== 'imitate' || s.phase.over) return;
    if (tracker?.round !== s.round) {
      tracker = {
        round: s.round,
        imitation: createImitationTracker(smileyOf(s), baselineOf(baselineFrames)),
      };
      bestPhoto = null;
      lastPhotoAt = -Infinity;
    }
    const sample = tracker.imitation.push(frame.t, frame.blendshapes);
    showLive(sample.live, tracker.imitation.best(), performance.now());
    if (sample.improved && frame.t - lastPhotoAt >= SMILEY_PHOTO_MS) {
      const data = takeSnapshot();
      if (data) {
        bestPhoto = { round: s.round, data };
        lastPhotoAt = frame.t;
      }
    }
  };

  let handWarm = false;
  /** Chifoumi : modèle de la main chargé dès l'arrivée de l'ami (le « chi-fou » ne dure que 3 s). */
  const warmHand = (): void => {
    if (handWarm) return;
    handWarm = true;
    void handSource.start().catch(() => undefined);
    handSource.stop(); // le chargement continue ; la boucle, elle, ne démarre pas
  };

  // --- Chifoumi : geste lu en continu du « chi-fou » au MI, verrouillé par la machine --------
  const throwBuffer = createThrowBuffer();
  const throwStatus = h('p', { class: 'pinch-status', testid: 'throw-status', role: 'status' });
  const showThrow = (gesture: Throw | null): void => {
    const key = gesture ?? 'none';
    if (throwStatus.dataset['gesture'] === key) return;
    throwStatus.dataset['gesture'] = key;
    throwStatus.textContent = gesture
      ? `${THROW_EMOJI[gesture]} ${THROW_NAME[gesture]}`
      : 'Montre ta main à la caméra';
  };

  // --- Détection locale ---------------------------------------------------------
  let monitor: ExpressionMonitor | null = null;
  let calibrationFps: number | undefined;

  const runtime = createGameRuntime(createInitialState(), {
    send: (m) => {
      link.send(m);
    },
    schedule: later,
    startCalibration: () => {
      const profile = profileFor(runtime.state.mode);
      if (monitor?.profile !== profile)
        monitor = createExpressionMonitor(profile, { calibrationMs: 3000 });
      monitor.calibrate(performance.now());
    },
    startHandTracking: () => {
      pinch.reset();
      showPinch({ hand: false, pinching: false, progress: 0, grabbed: false });
      throwBuffer.reset();
      showThrow(null);
      offHand ??= handSource.subscribe((f) => {
        if (runtime.state.game === 'chifoumi') {
          showThrow(throwBuffer.push(f.t, f.landmarks));
          return;
        }
        const st = pinch.push(f.t, f.landmarks);
        showPinch(st);
        if (st.grabbed) runtime.dispatch({ type: 'localGrab' });
      });
      void handSource.start().catch(() => undefined);
    },
    stopHandTracking: () => {
      handSource.stop();
      offHand?.();
      offHand = null;
    },
    // Visage perdu : j'envoie mon ralenti à l'ami qui doit voter.
    sendReplay: (round) => {
      shareReplay(round);
    },
    submitImitation: (round) => {
      const score = tracker?.round === round ? tracker.imitation.best() : 0;
      runtime.dispatch({ type: 'localImitation', score });
      // Pas de meilleur moment (grimace jamais tenue) : la photo de maintenant.
      const data = bestPhoto?.round === round ? bestPhoto.data : takeSnapshot();
      if (!data) return;
      snapshots.me = { round, url: JPEG + data };
      link.send({ type: 'snapshot', round, data });
      showSnapshots(runtime.state.round);
    },
    lockThrow: (round) => {
      const gesture = throwBuffer.lock(performance.now()) ?? 'none';
      const photo = takeSnapshot(); // la même image que le geste verrouillé, ou presque
      runtime.dispatch({ type: 'localThrow', gesture });
      if (!photo) return;
      snapshots.me = { round, url: JPEG + photo };
      link.send({ type: 'snapshot', round, data: photo });
      showSnapshots(runtime.state.round);
    },
  });
  runtime.dispatch({
    type: 'create',
    role: options.role,
    mode: options.mode,
    game: options.game,
    // Hôte : graine du tirage des smileys (l'invité reçoit celle de l'hôte).
    seed: Math.floor(Math.random() * 2 ** 31),
  });

  const onMonitorEvent = (e: MonitorEvent): void => {
    if (e.type === 'calibrated') runtime.dispatch({ type: 'calibrationSucceeded' });
    else if (e.type === 'calibrationFailed') {
      calibrationFps = e.fps;
      runtime.dispatch({ type: 'calibrationFailed', error: e.error });
    } else runtime.dispatch({ type: 'localLaugh', t: e.t, score: e.score });
  };

  // --- Filtre barbichette -------------------------------------------------------------
  // Le menton de l'ami est cherché sur SA vidéo telle que je la reçois (pas de décalage),
  // ou directement dans son maillage s'il a choisi de ne pas montrer sa vidéo.
  let remoteDetectionFps = 0;
  const remoteSource = createRemoteFaceSource(stage.remoteVideo, () =>
    stage.remoteShowsMesh() ? 0 : remoteDetectionFps,
  );
  let remoteLatest: { points: readonly Point3[]; t: number } | null = null;
  cleanups.push(
    remoteSource.subscribe((f) => {
      if (f.landmarks && f.landmarks.length > 0) remoteLatest = { points: f.landmarks, t: f.t };
    }),
  );
  void remoteSource.start().catch(() => undefined);
  const videoFace: RemoteFace = {
    latest: () => remoteLatest,
    sourceAspect: () =>
      stage.remoteVideo.videoWidth > 0
        ? stage.remoteVideo.videoWidth / stage.remoteVideo.videoHeight
        : 4 / 3,
    mirror: () => false,
  };
  const meshFace: RemoteFace = {
    latest: () => stage.remoteMesh(),
    sourceAspect: () => 4 / 3,
    mirror: () => true,
  };
  const overlay = createBarbichetteOverlay({
    face: () => (stage.remoteShowsMesh() ? meshFace : videoFace),
    active: () => barbichetteActive(runtime.state),
    enabled: () => filterOn,
    pinnedLevel: e2ePinnedLevel(),
  });
  stage.remoteFigure.append(overlay.element);
  const fpsSync = setInterval(() => {
    remoteDetectionFps = overlay.detectionFps();
  }, HEALTH_MS);
  cleanups.push(() => {
    clearInterval(fpsSync);
    overlay.stop();
    remoteSource.stop();
  });

  // --- Santé des flux → incidents -------------------------------------------------
  const linkMonitor = createLinkMonitor();
  const watcher = createIncidentWatcher();
  let lastFrameAt = performance.now();
  let lastFaceAt = performance.now();

  const onFrame = (frame: FaceFrame): void => {
    lastFrameAt = frame.t;
    if (frame.blendshapes) lastFaceAt = frame.t;
    if (monitor) for (const e of monitor.push(frame)) onMonitorEvent(e);
    if (runtime.state.game === 'smiley') onSmileyFrame(frame);
  };
  cleanups.push(stage.faceSource.subscribe(onFrame));
  void stage.faceSource.start();

  const health = (now: number): MediaHealth => {
    const track = localStream.getVideoTracks()[0];
    return {
      videoTrack: !track || track.readyState === 'ended' ? 'ended' : track.muted ? 'muted' : 'live',
      videoFrameAgeMs: now - lastFrameAt,
      faceMissingMs: now - lastFaceAt,
      channelOpen: link.isOpen(),
      heartbeatAgeMs: linkMonitor.heartbeatAge(now),
    };
  };

  // --- Réseau ----------------------------------------------------------------------------------
  /** Connexion obtenue : directe ou via un relais TURN (affichée avec l'aller-retour). */
  let linkKind: 'direct' | 'relay' | null = null;
  cleanups.push(
    link.onMessage((m) => {
      const { reply, clock } = linkMonitor.onMessage(m, performance.now());
      if (reply) link.send(reply);
      if (clock) runtime.dispatch({ type: 'clockSync', offset: clock.offset });
      if (m.type === 'replayChunk') onReplayChunk(m);
      if (m.type === 'snapshot') {
        snapshots.them = { round: m.round, url: JPEG + m.data };
        showSnapshots(runtime.state.round);
      }
      runtime.dispatch({ type: 'message', message: m });
    }),
    link.onClose(() => {
      runtime.dispatch({ type: 'peerLeft' });
    }),
  );
  const intervals = [
    setInterval(() => {
      link.send({ type: 'heartbeat', t: performance.now() });
    }, HEARTBEAT_MS),
    setInterval(() => {
      link.send(linkMonitor.makePing(performance.now()));
      void link.connectionKind?.().then((k) => {
        linkKind = k;
      });
    }, PING_MS),
    setInterval(() => {
      const now = performance.now();
      const change = watcher.update(health(now), now);
      if (change) runtime.dispatch(change);
      refreshIndicators(now);
    }, HEALTH_MS),
  ];
  link.send(linkMonitor.makePing(performance.now()));

  // --- Affichage --------------------------------------------------------------------
  let chantStart = 0;
  let chantRaf = 0;
  let cancelNextRound: (() => void) | null = null;

  const refreshIndicators = (now: number): void => {
    const age = linkMonitor.heartbeatAge(now);
    const clock = linkMonitor.clock();
    const via = linkKind === null ? '' : linkKind === 'relay' ? ' · via relais' : ' · directe';
    stats.textContent = clock
      ? `aller-retour ${clock.rtt.toFixed(0)} ms${via} · dernier signe de vie il y a ${age.toFixed(0)} ms`
      : 'synchronisation…';
    if (linkKind) stats.dataset['kind'] = linkKind;
    meshStats.textContent = stage.meshStats();
    const s = runtime.state;
    if (s.phase.name === 'ended') return;
    status.textContent = link.isOpen() && age > LOST_AFTER_MS ? 'Connexion perdue…' : 'Connecté !';
  };

  const button = (
    label: string,
    testid: string,
    onClick: () => void,
    secondary = false,
  ): HTMLButtonElement => {
    const b = h(
      'button',
      { type: 'button', class: secondary ? 'button secondary' : 'button', testid },
      label,
    );
    b.addEventListener('click', onClick);
    return b;
  };

  const renderChant = (s: GameState): HTMLElement => {
    const [lines, total] =
      s.game === 'chifoumi'
        ? [CHIFOUMI_CHANT, TIMINGS.chifoumiChantMs]
        : s.game === 'smiley'
          ? [SMILEY_COUNTDOWN, TIMINGS.smileyCountdownMs]
          : [chantLines(s.mode), TIMINGS.chantMs];
    const spans = lines.map((line) => line.map((word) => h('span', { class: 'word' }, word)));
    const box = h(
      'p',
      { class: 'karaoke', testid: 'karaoke' },
      ...spans.map((ws) => h('span', { class: 'line' }, ...ws.flatMap((w) => [w, ' ']))),
    );
    const tick = (): void => {
      const { line, word } = chantProgress(lines, performance.now() - chantStart, total);
      spans.forEach((ws, li) => {
        ws.forEach((w, wi) => w.classList.toggle('sung', li < line || (li === line && wi <= word)));
      });
      chantRaf = requestAnimationFrame(tick);
    };
    chantRaf = requestAnimationFrame(tick);
    return box;
  };

  const render = (s: GameState, previous: GameState | null): void => {
    stage.hideVideos(hidesVideo(s));
    score.textContent = `Toi ${s.score.me} – ${s.score.them} Ton ami`;
    filterToggle.hidden = s.game !== 'barbichette';
    score.hidden = s.phase.name === 'connected' || s.phase.name === 'lobby';
    cancelAnimationFrame(chantRaf);
    const p = s.phase;
    const texts = TEXTS[s.mode];
    let content: (Node | string)[] = [];
    switch (p.name) {
      case 'idle':
      case 'lobby':
      case 'connected':
        if (s.game === 'chifoumi') warmHand();
        content = [
          h('h2', {}, 'Ton ami est là !'),
          h('p', { testid: 'game-mode' }, ruleLabel(s)),
          s.meReady
            ? h('p', { class: 'status waiting' }, 'On attend ton ami')
            : button('Je suis prêt !', 'ready', () => {
                slapSound.unlock(); // geste de l'utilisateur : le son est autorisé
                runtime.dispatch({ type: 'localReady' });
              }),
        ];
        break;
      case 'calibrating':
        content = [
          h('h2', {}, 'Calibration'),
          h(
            'p',
            {},
            p.error ? calibrationMessage(s.mode, p.error, calibrationFps) : texts.calibrating,
          ),
        ];
        break;
      case 'grab':
        content =
          s.meGrabbed === s.round
            ? [
                h('h2', {}, 'Tu le tiens !'),
                h('p', { class: 'status waiting' }, 'Ton ami doit t’attraper aussi'),
              ]
            : [
                h('h2', {}, 'Attrape sa barbichette !'),
                h('p', {}, 'Pince ton pouce et ton index devant la caméra.'),
                pinchStatus,
                ...(p.showButton
                  ? [
                      h('p', { class: 'muted' }, 'La caméra ne voit pas ta main ?'),
                      button('J’attrape !', 'grab', () => {
                        runtime.dispatch({ type: 'localGrab' });
                      }),
                    ]
                  : []),
              ];
        break;
      case 'chant':
        if (previous?.phase.name !== 'chant') {
          chantStart = performance.now();
          // Détecteur réarmé : un fou rire déjà commencé sera bien vu.
          if (monitor?.calibration) monitor.setCalibration(monitor.calibration);
          baselineFrames = [];
        }
        content =
          s.game === 'chifoumi'
            ? [h('h2', {}, 'Prépare ton geste…'), renderChant(s), throwStatus]
            : s.game === 'smiley'
              ? [
                  h('h2', {}, 'Visage neutre…'),
                  h('p', {}, 'On regarde ton visage au repos : le smiley arrive !'),
                  renderChant(s),
                ]
              : [h('h2', {}, 'Tous en chœur !'), renderChant(s)];
        break;
      case 'imitate': {
        const id = smileyOf(s);
        smileyEmoji.textContent = SMILEYS[id].emoji;
        smileyEmoji.dataset['smiley'] = id;
        smileyLabel.textContent = SMILEYS[id].label;
        if (previous?.phase.name !== 'imitate') {
          imitateStart = performance.now();
          showLive(0, 0, imitateStart);
        }
        content = [
          h('h2', {}, 'Imite ce smiley !'),
          smileyEmoji,
          smileyLabel,
          ...(p.over
            ? [h('p', { class: 'status waiting' }, 'Temps écoulé ! On compare…')]
            : [liveEl, bestEl, timeLeft]),
        ];
        break;
      }
      case 'throw':
        content = [
          h('h2', { class: 'shout', testid: 'shout' }, 'MI !'),
          s.throws.me === null
            ? throwStatus
            : h(
                'p',
                { class: 'status waiting' },
                `Ton coup : ${THROW_EMOJI[s.throws.me]} — on attend ton ami`,
              ),
        ];
        break;
      case 'duel':
        content = [h('h2', { testid: 'duel' }, 'Duel !'), h('p', {}, ruleLabel(s))];
        break;
      case 'paused': {
        const why =
          s.myIncident === 'network'
            ? 'La connexion est coupée.'
            : s.myIncident === 'camera'
              ? 'Ta caméra ne répond plus.'
              : 'Ton ami a un souci de caméra.';
        content = [
          h('h2', {}, 'Pause'),
          h('p', {}, `${why} On reprend dès que possible (manche rejouée au-delà de 15 s).`),
        ];
        break;
      }
      case 'review':
        content =
          p.lostFace === 'them'
            ? [
                h('h2', {}, 'Ton ami a disparu du cadre !'),
                h('p', {}, 'Main devant le visage, tête tournée… Tu valides sa défaite ?'),
                h(
                  'div',
                  { class: 'actions' },
                  button(
                    'On rejoue la manche',
                    'vote-replay',
                    () => {
                      runtime.dispatch({ type: 'localVote', verdict: 'replay' });
                    },
                    true,
                  ),
                  button('Défaite validée', 'vote-defeat', () => {
                    runtime.dispatch({ type: 'localVote', verdict: 'defeat' });
                  }),
                ),
              ]
            : [
                h('h2', {}, 'Tu as disparu du cadre…'),
                h('p', { class: 'status waiting' }, 'Ton ami décide'),
              ];
        break;
      case 'result': {
        const chifoumi = s.game === 'chifoumi';
        const smiley = s.game === 'smiley';
        const loser = punished(s);
        if (loser === 'me') shareReplay(s.round);
        const side = loser === 'them' ? 'remote' : loser === 'me' ? 'local' : null;
        if (side && previous?.phase.name !== 'result') {
          // D'abord la tapette, puis le ralenti.
          slapLayers[side].play();
          if (soundOn) slapSound.play();
          replaySlot.hidden = true;
          later(SLAP_MS, () => {
            replaySlot.hidden = false;
          });
        }
        const title = (
          smiley
            ? {
                won: 'Tu gagnes la manche !',
                lost: 'Tu perds la manche…',
                tie: 'Égalité !',
                draw: 'Égalité !',
              }
            : chifoumi
              ? {
                  won: 'Tu gagnes le coup !',
                  lost: 'Tu perds le coup…',
                  tie: 'Égalité, on rejoue !',
                  draw: 'Égalité, on rejoue !',
                }
              : {
                  won: 'Tu as gagné la manche !',
                  lost: 'Tu as perdu la manche…',
                  tie: 'Égalité !',
                  draw: 'Personne n’a craqué : match nul.',
                }
        )[p.outcome];
        content = [h('h2', { testid: 'round-result' }, title)];
        if (chifoumi) {
          const { me, them } = s.throws;
          content.push(
            h(
              'p',
              { class: 'throws', testid: 'throws' },
              `Toi ${THROW_EMOJI[me ?? 'none']} – ${THROW_EMOJI[them ?? 'none']} Ton ami`,
            ),
            snapshotsEl,
          );
          showSnapshots(s.round);
        }
        if (smiley) {
          const id = smileyOf(s);
          content.push(
            h(
              'p',
              { class: 'muted' },
              `Manche ${s.played}/3 · ${SMILEYS[id].emoji} ${SMILEYS[id].label}`,
            ),
            h(
              'p',
              { class: 'throws', testid: 'imitation-scores' },
              `Toi ${s.imitation.me ?? 0} % – ${s.imitation.them ?? 0} % Ton ami`,
            ),
            snapshotsEl,
          );
          showSnapshots(s.round);
        }
        if (p.matchOver) {
          const winner = matchWinner(s);
          content.push(
            h(
              'p',
              { class: 'record', testid: 'match-result' },
              {
                me: 'Tu remportes le match !',
                them: 'Ton ami remporte le match.',
                draw: 'Match nul !',
              }[winner],
            ),
            ...(smiley
              ? [
                  h(
                    'p',
                    { class: 'muted', testid: 'imitation-total' },
                    `Total : toi ${s.points.me} – ${s.points.them} ton ami`,
                  ),
                ]
              : []),
            s.rematch.me
              ? h('p', { class: 'status waiting' }, 'On attend ton ami')
              : h(
                  'div',
                  { class: 'actions' },
                  button('Accueil', 'menu', options.onQuit, true),
                  button('Revanche !', 'rematch', () => {
                    runtime.dispatch({ type: 'localRematch' });
                  }),
                ),
          );
        } else {
          content.push(
            h(
              'p',
              { class: 'muted' },
              chifoumi ? 'Coup suivant…' : 'Manche suivante dans un instant…',
            ),
          );
          if (previous?.phase.name !== 'result') {
            const delay = chifoumi ? NEXT_THROW_MS : smiley ? NEXT_SMILEY_MS : NEXT_ROUND_MS;
            cancelNextRound = later(delay, () => {
              runtime.dispatch({ type: 'nextRound' });
            });
          }
        }
        break;
      }
      case 'ended': {
        const text = {
          quit: 'Partie terminée.',
          peerLeft: 'Ton ami a quitté la partie.',
          versionMismatch: 'Vos versions du jeu diffèrent : rechargez tous les deux la page.',
        }[p.reason];
        status.textContent = text;
        content = [h('h2', {}, text), button('Retour à l’accueil', 'menu', options.onQuit)];
        break;
      }
    }
    if (p.name !== 'result') {
      cancelNextRound?.();
      cancelNextRound = null;
    }
    // Ralenti : celui qui a disparu du cadre (vote) ou celui qui reçoit la tapette.
    const loser = punished(s);
    const target =
      p.name === 'review'
        ? replayTarget(p, s.round)
        : loser
          ? { round: s.round, who: loser }
          : null;
    showReplay(target);
    banner.replaceChildren(...content, ...(target ? [replaySlot] : []));
  };

  // Rendu seulement si quelque chose de visible change (pas à chaque synchro d'horloge).
  cleanups.push(
    runtime.subscribe((s, previous) => {
      if (viewChanged(previous, s)) render(s, previous);
    }),
  );
  render(runtime.state, null);
  runtime.dispatch({ type: 'peerConnected' });

  return {
    inProgress: () => {
      const p = runtime.state.phase;
      return p.name !== 'ended' && !(p.name === 'result' && p.matchOver);
    },
    quit: () => {
      runtime.dispatch({ type: 'quit' });
      link.close();
    },
    stop: () => {
      runtime.dispose();
      for (const id of intervals) clearInterval(id);
      for (const id of timers) clearTimeout(id);
      cancelAnimationFrame(chantRaf);
      disposed = true;
      handSource.stop();
      offHand?.();
      clearReplay();
      recorder.stop();
      slapLayers.remote.stop();
      slapLayers.local.stop();
      for (const off of cleanups) off();
      stage.dispose();
    },
  };
}
